import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import TextEditor from "../../common/TextEditor";
import FileUploader from "../../common/FileUploader";
import MultiSelectDropdown from "../../common/MultiSelectDropdown";
import taskService from "../../../services/taskService";
import userService from "../../../services/userService";
import { X } from 'lucide-react';
import { useNotification } from "../../../contexts/NotificationContext";
import { useAuth } from "../../../contexts/AuthContext";

const TaskModal = ({ isOpen, headerText, mode, data, statuses, onCancel, onRefresh }) => {  
  const { createTask, taskDetails, updateTask } = taskService();
  const { userList, userGroupList } = userService();
  const { user } = useAuth();
  const [loggedInUserId, setLoggedInUserId] = useState("");
  const [isLoggedInUserSuperAdmin, setIsLoggedInUserSuperAdmin] = useState("");
  
  const [errors, setErrors] = useState({});
  const taskIniState = {
    taskName: '',
    dueDate: (() => {
      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + 7);
      return dueDate.toISOString().split("T")[0];
    })(),
    description: '',
    attachments: { draftAttachmentList: [] },
    assignedUser: [],
    assignedGroup: [],
    comments: '',
    status: 'UNASSIGNED'
  };
  const [taskState, setTaskState] = useState(taskIniState);
  const inputRef = useRef(null);
  const [userOptions, setUserOptions] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [commentInput, setCommentInput] = useState('');
  const [commentsList, setCommentsList] = useState([]);
  const [hasPendingUploads, setHasPendingUploads] = useState(false);
  const { showNotification } = useNotification();

  const hasFetched = useRef(false);
  const isViewOnly = mode === 'view';

  useEffect(() => {
    const fetchTaskDetails = async () => {
      if (isOpen && (mode === 'edit' || mode === 'view') && data?.taskId && !hasFetched.current) {
        hasFetched.current = true;
        try {
          const response = await taskDetails(data);
          const taskData = response?.data;
          setTaskState({
            taskName: taskData?.taskName,
            dueDate: taskData?.dueDate,
            description: taskData?.taskDescription,
            attachments: taskData?.attachments || [],
            assignedByUser: taskData?.assignedByUser, 
            assignedUser: taskData?.assignedToUser ? [{ id: taskData.assignedToUser }] : [],
            assignedGroup: taskData?.assignedToGroup ? [{ id: taskData.assignedToGroup }] : [],
            comments: taskData?.comments || '',
            status: taskData?.status,
          });          
          setCommentsList(taskData?.comments || []);
        } catch (error) {
          console.error("Failed to fetch task details:", error);
        }
        if (mode === 'edit') {
          inputRef.current?.focus();
        }
      }
      if (!isOpen) {
        hasFetched.current = false;
      }
    };
    fetchTaskDetails();
  }, [isOpen, mode, data?.taskId, taskDetails]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onCancel();
      }
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      if (mode === 'create') {
        inputRef.current?.focus();
      }
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onCancel, mode]);

  useEffect(() => {
    if (!isOpen) {
      setTaskState(taskIniState);
      setSelectedUsers([]);
      setSelectedGroups([]);
      setCommentInput('');
      setCommentsList([]);
    }
  }, [isOpen]);

  useEffect(() => {      
      if (user) {        
        setLoggedInUserId(user.id || "");
        setIsLoggedInUserSuperAdmin(user.role == "SUPER_ADMIN");
      }
    }, [user]);

  useEffect(() => {
    let mounted = true;

    const fetchUsers = async () => {
      try {
        const users = await userList();
        if (!mounted) return;
        const formattedUsers = users.data.map((user) => ({
          id: user.id,
          name: `${user.firstName} ${user.lastName}`,
        }));
        setUserOptions(formattedUsers);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    const fetchUserGroups = async () => {
      try {
        const userGroups = await userGroupList();
        if (!mounted) return;
        const formattedUserGroups = userGroups.map((userGroup) => ({
          id: userGroup.name,
          name: `${userGroup.displayName}`,
        }));
        setGroupOptions(formattedUserGroups);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    fetchUsers();
    fetchUserGroups();

    return () => {
      mounted = false;
    };
    // userList / userGroupList are stable in practice but may be recreated by service hooks;
    // intentionally run once on mount to avoid infinite loops
  }, []);

  useEffect(() => {
    if (taskState.assignedUser.length > 0 && userOptions.length > 0) {
      const selected = taskState.assignedUser
        .map(user => userOptions.find(u => u.id === user.id))
        .filter(Boolean);
      setSelectedUsers(selected);
    }
    if (taskState.assignedGroup.length > 0 && groupOptions.length > 0) {
      const selected = taskState.assignedGroup
        .map(group => groupOptions.find(u => u.id === group.id))
        .filter(Boolean);
      setSelectedGroups(selected);
    }
  }, [taskState, userOptions, groupOptions]);

  const handleInputChange = (field, value) => {
    if (field === 'comments' && mode !== 'view') {
      setCommentInput(value);
    } else {
      setTaskState((prev) => ({ ...prev, [field]: value }));
    }
  };

  const setTaskStatus = (user, group) => {
    if (user.length == 0 && group.length == 0) {
      setTaskState((prev) => ({ ...prev, status: 'UNASSIGNED' }));
    } else {
      setTaskState((prev) => ({ ...prev, status: 'ASSIGNED' }));
    }
  };

  const handleAttachmentsChange = (attachments) => {
    setTaskState((prev) => ({ ...prev, attachments }));
  };

  const handleUserChange = (selected) => {
    setSelectedUsers(selected);
    setTaskState((prev) => ({ ...prev, assignedUser: selected }));
    setTaskStatus(selected, taskState.assignedGroup);
  };

  const handleGroupChange = (selected) => {
    setSelectedGroups(selected);
    setTaskState((prev) => ({ ...prev, assignedGroup: selected }));
    setTaskStatus(taskState.assignedUser, selected);
  };

  const handleSubmit = async () => {
    const newErrors = {};
    const { taskName, dueDate, assignedUser, assignedGroup, status } = taskState;
    if (!taskName.trim()) newErrors.taskName = "Task Name is required.";
    if (!dueDate) newErrors.dueDate = "Due Date is required.";
    if (assignedUser.length === 0 && assignedGroup.length === 0) {
      newErrors.assignedUser = "At least one User or Group must be assigned.";
      newErrors.assignedGroup = "At least one User or Group must be assigned.";
    }
    if (!status) newErrors.status = "Task Status is required.";
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    try {
      if (mode === 'create') {
        const taskData = {
          baseTask: {
            name: taskName,
            dueDate,
            description: taskState.description,
            isRepeat: false,
            isExpired: false,
            assignedToUser: taskState.assignedUser.map(user => user.id),
            assignedToGroup: taskState.assignedGroup.map(group => group.id),
            status: taskState.status,
            comment: commentInput ? commentInput : null
          },
          attachmentsInDraft: taskState.attachments
        };
        await createTask(taskData);
        showNotification(`Task created successfully!`, "success");
        onCancel();
        onRefresh();
      } else if (mode === 'edit') {
        const taskData = {
          taskId: data?.taskId,
          name: taskState.taskName,
          dueDate: taskState.dueDate || null,
          description: taskState.description || '',
          assignedToUser: taskState.assignedUser?.[0]?.id || null,
          status: taskState.status,
          deleteAttachments: {},
          newAttachments: {
            draftAttachmentList: taskState.attachments?.filter(att => !att.id) || []
          },
          newComment: commentInput ? { taskId: data?.taskId, content: commentInput } : null
        };
        await updateTask(taskData);
        showNotification(`Task updated successfully!`, "success");
        onCancel();
        onRefresh();
      }
    } catch (error) {
      showNotification(`Error in creating/updating task: ${error.message}`, "error");
    }
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="task-modal-title"
    >
      <div
        className="absolute inset-0 bg-dark/80"
        onClick={onCancel}
        aria-hidden="true"
      />
      <div
        className="relative z-10 w-full max-w-full sm:max-w-[750px] bg-white rounded-t-lg sm:rounded-[20px] shadow-lg overflow-hidden max-h-[90vh] flex flex-col"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        role="document"
        aria-labelledby="task-modal-title"
      >
        <div className="bg-primary text-white px-4 py-3 sm:py-4 flex justify-between items-center">
          <h3 id="task-modal-title" className="text-lg sm:text-xl font-semibold truncate">
            {headerText}
          </h3>
          <button
            onClick={onCancel}
            aria-label="Close"
            className="p-2 rounded-md text-white hover:text-red-300 focus:outline-none focus:ring-2 focus:ring-white"
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-4 py-5 sm:px-[32px] overflow-auto">
          <div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <DefaultColumn>
                <DefaultInput
                  label="Task Name"
                  value={taskState.taskName}
                  onChange={(e) => handleInputChange('taskName', e.target.value)}
                  disabled={isViewOnly}
                  ref={inputRef}
                />
                {errors.taskName && <p className="text-red-500 text-xs mt-1">{errors.taskName}</p>}
              </DefaultColumn>
              <DefaultColumn>
                <DefaultDate
                  label="Due Date"
                  value={taskState.dueDate}
                  onChange={(e) => handleInputChange('dueDate', e.target.value)}
                  disabled={isViewOnly}
                />
                {errors.dueDate && <p className="text-red-500 text-xs mt-1">{errors.dueDate}</p>}
              </DefaultColumn>
            </div>

            <div className="grid grid-cols-1">
              <label className="mb-[10px] block text-xs font-medium text-[#404145] text-dark">
                Description
              </label>
              <div className="mb-5">
                <TextEditor
                  value={taskState.description}
                  onChange={(content) => handleInputChange('description', content)}
                  disabled={isViewOnly}
                />
              </div>
            </div>

            <div className="grid grid-cols-1">
              <label className="mt-10 mb-[10px] text-xs font-medium text-[#404145] text-dark">
                Attachment(s)
              </label>
              <div className="mb-5">
                <FileUploader
                  initialAttachments={{ draftAttachmentList: taskState.attachments }}
                  onAttachmentsChange={handleAttachmentsChange}
                  onPendingUploadChange={setHasPendingUploads}
                  disabled={isViewOnly}
                  taskId={data?.taskId}
                />
              </div>
            </div>

            <div className={`grid ${mode === 'create' ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'} gap-4`}>
              <DefaultColumn>
                <MultiSelectDropdown
                  label="Users"
                  options={userOptions}
                  value={selectedUsers}
                  onChange={handleUserChange}
                  placeholder="Select Users..."
                  isViewOnly={isViewOnly}
                  anchorToParent={true}
                />
                {errors.assignedUser && <p className="text-red-500 text-xs mt-1">{errors.assignedUser}</p>}
              </DefaultColumn>
              {mode === 'create' && (
                <DefaultColumn>
                  <MultiSelectDropdown
                    label="Groups"
                    options={groupOptions}
                    value={selectedGroups}
                    onChange={handleGroupChange}
                    placeholder="Select Groups..."
                    isViewOnly={isViewOnly}
                  />
                  {errors.assignedGroup && <p className="text-red-500 text-xs mt-1">{errors.assignedGroup}</p>}
                </DefaultColumn>
              )}
            </div>

            <div className={`grid ${mode === 'create' ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'} gap-4`}>
              <DefaultColumn>
                <DefaultSelect
                  label="Task Status"
                  value={taskState.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  disabled={isViewOnly}
                  options={statuses}
                  assignedByUser = {taskState.assignedByUser}
                  loggedInUserId = {loggedInUserId}
                  isLoggedInUserSuperAdmin = {isLoggedInUserSuperAdmin}
                />
                {errors.status && <p className="text-red-500 text-xs mt-1">{errors.status}</p>}
              </DefaultColumn>
            </div>

            <div className="grid grid-cols-1">
              <div className="mt-0">
                <label className="mb-2 block text-xs font-medium">
                  Comment
                </label>
                <textarea
                  rows="1"
                  placeholder="Add comment"
                  className="w-full rounded-md border border-stroke bg-transparent p-5 text-sm text-dark outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-gray-2"
                  value={mode === 'view' ? '' : commentInput}
                  onChange={(e) => handleInputChange('comments', e.target.value)}
                  disabled={isViewOnly}
                ></textarea>
                {commentsList.length > 0 && (
                  <div className="mt-4 space-y-3">
                    {commentsList.map((comment) => (
                      <div key={comment.id} className="p-3 border rounded-md bg-gray-50">
                        <p className="text-xs text-gray-500">{new Date(comment.timestamp).toLocaleString()}</p>
                        <p className="text-sm text-dark">{comment.content}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 border-t px-4 py-4">
              <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-center">
                <div className="w-full sm:w-1/2 px-0 sm:px-3">
                  <button
                    className="w-full rounded-md border border-stroke px-4 py-3 text-sm font-medium text-dark bg-white hover:border-red-600 hover:bg-red-600 hover:text-white transition-colors focus:outline-none"
                    onClick={onCancel}
                  >
                    {mode === 'view' ? 'Cancel' : 'Cancel'}
                  </button>
                </div>

                {mode !== 'view' ? (
                  <div className="w-full sm:w-1/2 px-0 sm:px-3">
                    <button
                      className={`w-full rounded-md px-4 py-3 text-sm font-medium text-white bg-primary hover:bg-blue-dark transition-colors focus:outline-none ${hasPendingUploads ? 'opacity-50 cursor-not-allowed' : ''}`}
                      onClick={handleSubmit}
                      disabled={hasPendingUploads}
                    >
                      Save
                    </button>
                  </div>
                ) : null}
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

const DefaultColumn = ({ children }) => (
  <div>
    <div className="mb-5">{children}</div>
  </div>
);

const DefaultInput = React.forwardRef(({ label, value, onChange, disabled }, ref) => (
  <>
    <label className="mb-2 mr-1 text-xs font-medium">{label}</label>
    <span className="text-red-500">*</span>
    <input
      ref={ref}
      type="text"
      value={value}
      onChange={onChange}
      disabled={disabled}
      className="w-full rounded-md border px-5 py-2 text-sm"
    />
  </>
));

const DefaultDate = ({ label, value, onChange, disabled }) => (
  <>
    <label className="mb-2 mr-1 text-xs font-medium">{label}</label>
    <span className="text-red">*</span>
    <input
      type="date"
      value={value}
      onChange={onChange}
      disabled={disabled}
      className="w-full rounded-md border px-5 py-2 text-sm"
    />
  </>
);

const DefaultSelect = ({ label, value, onChange, disabled, options, assignedByUser, loggedInUserId, isLoggedInUserSuperAdmin }) => (
  <>
    <label className="mb-2 mr-1 text-xs font-medium">{label}</label>
    <span className="text-red">*</span>
    <select
      value={value}
      onChange={onChange}
      disabled={disabled}
      className={`w-full rounded-md border px-5 py-2 text-sm ${disabled ? "bg-gray-200 cursor-not-allowed" : "cursor-pointer"}`}
    >
      {options && 
       options
        .filter(status => {          
          if (!disabled && status.name === "Completed" && (assignedByUser !== loggedInUserId && isLoggedInUserSuperAdmin == false)) {
            return false;
          }
          return true;
        })
       .map((status, index) => (
        <option key={index} value={status.value}>
          {status.name}
        </option>
      ))}
    </select>
  </>
);

export default TaskModal;
